import capitalize from "lodash/capitalize";
import cloneDeep from "lodash/cloneDeep";
import { keys } from "ramda";
import { Logger } from "../Logger";
import { gatherVideoClientDataSource } from "../Globals";
import { buildErrorContext, ErrorContext } from "gather-common/dist/src/public/utils";
import { GVCInternalEvent } from "../EventEmitter/GVCEventEmitter";
import { GVCInternalMediasoupEvent } from "../EventEmitter/MediasoupEvents";
const CHECK_CONSUMER_RATE_MS = 20000;
const ZERO_CONSUMER_KIND_STATS = {
    missingPacketsEventCount: 0,
    totalLifespanMS: 0,
};
const ZERO_WEB_RTC_METRICS = {
    audio: cloneDeep(ZERO_CONSUMER_KIND_STATS),
    video: cloneDeep(ZERO_CONSUMER_KIND_STATS),
    screen: cloneDeep(ZERO_CONSUMER_KIND_STATS),
    sound: cloneDeep(ZERO_CONSUMER_KIND_STATS),
};
async function getPacketsReceived(consumer) {
    if (consumer.closed)
        return;
    let stats;
    try {
        stats = await consumer.getStats();
    }
    catch (err) {
        Logger.error("getStats error", buildErrorContext(err));
        return;
    }
    let packetsReceived;
    stats.forEach((entry) => {
        if (entry.type === "inbound-rtp") {
            if (packetsReceived !== undefined && entry.packetsReceived !== undefined) {
                Logger.error("Consumer unexpectedly has multiple inbound-rtp stats");
            }
            packetsReceived = entry.packetsReceived;
        }
    });
    return packetsReceived;
}
export class MediasoupMetricsCollector {
    _eventEmitter;
    _consumers = {
        audio: {},
        video: {},
        screen: {},
        sound: {},
    };
    _numUnmutedConsumers = {
        audio: 0,
        video: 0,
        screen: 0,
        sound: 0,
    };
    _metrics = cloneDeep(ZERO_WEB_RTC_METRICS);
    _lastLifespanUpdateTime = 0;
    _started = false;
    constructor(eventEmitter) {
        this._eventEmitter = eventEmitter;
        this._eventEmitter.addEventListener(GVCInternalEvent.ConsumerCreated, this.consumerCreateListener);
        this._eventEmitter.addEventListener(GVCInternalEvent.ConsumerDisabled, this.consumerDisableListener);
        this._eventEmitter.addEventListener(GVCInternalEvent.ConsumerEnabled, this.consumerEnableListener);
        this._eventEmitter.addEventListener(GVCInternalEvent.ConsumerDestroyed, this.consumerDestroyListener);
    }
    start() {
        if (this._started)
            return;
        this._started = true;
        this.resetCollection();
        this._forEachConsumer(this._startConsumerStatsInterval);
    }
    stop() {
        if (!this._started)
            return;
        this._started = false;
        this._forEachConsumer(this._stopConsumerStatsInterval);
    }
    consumerCreateListener = (context) => {
        const { kind, consumer, playerId } = context;
        if (!consumer)
            return;
        if (this._consumers[kind][consumer.id]) {
            Logger.error(`Created consumers with the same ID `, new ErrorContext({ consumerId: consumer.id }));
            this._deleteConsumer(kind, consumer.id);
        }
        this._updateTime();
        const consumerData = {
            consumer,
            playerId,
            missingPacketsLastInterval: false,
            lastPacketsReceived: 0,
            muted: new Set(),
        };
        this._consumers[kind][consumer.id] = consumerData;
        this._startConsumerStatsInterval(kind, consumerData);
    };
    consumerDestroyListener = (context) => {
        const { kind, consumer } = context;
        if (!consumer)
            return;
        this._deleteConsumer(kind, consumer.id);
    };
    consumerDisableListener = (context) => {
        const { kind, consumer, side } = context;
        if (!consumer)
            return;
        if (!side)
            return;
        this.setConsumerMute(side, kind, consumer, true);
    };
    consumerEnableListener = (context) => {
        const { kind, consumer, side } = context;
        if (!consumer)
            return;
        if (!side)
            return;
        this.setConsumerMute(side, kind, consumer, false);
    };
    consumerCount(kind) {
        return Object.keys(this._consumers[kind]).length;
    }
    outputMetrics() {
        if (!this._started)
            return [];
        this._updateTime();
        const flattenedMetrics = {
            consumingScreenShareCount: this.consumerCount("screen"),
        };
        Object.entries(this._metrics).forEach(([kind, stats]) => {
            const kindStr = capitalize(kind);
            flattenedMetrics[`missing${kindStr}ConsumerPacketsEventCount`] =
                stats.missingPacketsEventCount;
            flattenedMetrics[`total${kindStr}ConsumerLifespanMS`] = stats.totalLifespanMS;
        });
        return [{ name: "video-client", values: flattenedMetrics }];
    }
    resetCollection() {
        this._lastLifespanUpdateTime = gatherVideoClientDataSource.performance().now();
        this._metrics = cloneDeep(ZERO_WEB_RTC_METRICS);
    }
    setConsumerMute(side, kind, consumer, muted) {
        const consumerData = this._consumers[kind]?.[consumer.id];
        if (!consumerData)
            return;
        if (muted) {
            consumerData.muted.add(side);
        }
        else {
            consumerData.muted.delete(side);
        }
        this._updateTime();
        if (consumerData.muted.size === 0) {
            this._startConsumerStatsInterval(kind, consumerData);
        }
        else {
            this._stopConsumerStatsInterval(kind, consumerData);
        }
    }
    _updateTime() {
        const currentTime = gatherVideoClientDataSource.performance().now();
        const changeInTime = currentTime - this._lastLifespanUpdateTime;
        keys(this._numUnmutedConsumers).forEach((kind) => {
            this._metrics[kind].totalLifespanMS += changeInTime * this._numUnmutedConsumers[kind];
        });
        this._lastLifespanUpdateTime = currentTime;
    }
    _forEachConsumer(fn) {
        keys(this._consumers).forEach((kind) => {
            Object.values(this._consumers[kind]).forEach((consumerData) => {
                fn(kind, consumerData);
            });
        });
    }
    _deleteConsumer(kind, consumerId) {
        const consumerData = this._consumers[kind][consumerId];
        if (!consumerData)
            return;
        this._updateTime();
        delete this._consumers[kind][consumerId];
        this._stopConsumerStatsInterval(kind, consumerData);
    }
    _startConsumerStatsInterval = (kind, consumerData) => {
        if (!this._started ||
            consumerData.collectStatsInterval !== undefined ||
            consumerData.muted.size > 0) {
            return;
        }
        const consumer = consumerData.consumer;
        consumerData.collectStatsInterval = window.setInterval(async () => {
            const packetsReceived = await getPacketsReceived(consumer);
            if (packetsReceived === undefined) {
                Logger.error("Missing packetsReceived", new ErrorContext({ consumerId: consumer.id }));
                return;
            }
            const prevPacketsReceived = consumerData.lastPacketsReceived;
            if (packetsReceived < prevPacketsReceived) {
            }
            else if (packetsReceived === prevPacketsReceived) {
                if (!consumerData.missingPacketsLastInterval) {
                    consumerData.missingPacketsLastInterval = true;
                    this._metrics[kind].missingPacketsEventCount++;
                    Logger.error("Packets not received", new ErrorContext({
                        kind,
                        srcId: consumerData.playerId,
                        consumerId: consumer.id,
                    }));
                    const context = { playerId: consumerData.playerId, kind };
                    this._eventEmitter.publishEvent(GVCInternalMediasoupEvent.PacketsNotReceived, context);
                }
            }
            else {
                consumerData.lastPacketsReceived = packetsReceived;
                consumerData.missingPacketsLastInterval = false;
            }
        }, CHECK_CONSUMER_RATE_MS);
        this._numUnmutedConsumers[kind] += 1;
    };
    _stopConsumerStatsInterval = (kind, consumerData) => {
        if (consumerData.collectStatsInterval === undefined)
            return;
        window.clearInterval(consumerData.collectStatsInterval);
        consumerData.collectStatsInterval = undefined;
        this._numUnmutedConsumers[kind] -= 1;
    };
}
//# sourceMappingURL=MediasoupMetricsCollector.js.map